import { getLangAndCurrency } from '../../services/Lang&Currency/Lang&Currency';
import { create } from 'zustand';
import defaultMenuData from '../../data/menuItems.json';

// Zustand store cho đa ngôn ngữ và tiền tệ
export const useLanguageStore = create((set, get) => ({
    currentLanguage: localStorage.getItem('preferred-language') || 'vi',
    translations: {
        // Khởi tạo với default data để có ngôn ngữ tạm thời ngay lập tức
        vi: defaultMenuData.content
    },
    currency: JSON.parse(localStorage.getItem('preferred-currency')) || {
        code: 'VND',
        symbol: '₫',
        rate: 26000,
        default_currency: 'USD'
    },
    loading: false,
    error: null,
    isInitialized: false, // Đánh dấu đã load xong lần đầu chưa

    // Keep track of pending requests to prevent duplicate fetches
    pendingRequests: {},

    // Lưu translations vào localStorage để cache
    saveTranslationsToStorage: (language, translations) => {
        try {
            const storageKey = `translations_${language}`;
            const dataToSave = {
                language,
                content: translations,
                timestamp: Date.now(),
                version: '1.0'
            };
            localStorage.setItem(storageKey, JSON.stringify(dataToSave));
        } catch (error) {
            console.error('Error saving translations to localStorage:', error);
        }
    },

    // Lấy translations từ localStorage
    loadTranslationsFromStorage: (language) => {
        try {
            const storageKey = `translations_${language}`;
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const data = JSON.parse(saved);
                // Kiểm tra xem data có hợp lệ không và không quá cũ (7 ngày)
                const isExpired = Date.now() - data.timestamp > 7 * 24 * 60 * 60 * 1000; // 7 ngày
                if (!isExpired && data.content) {
                    return data.content;
                }
            }
        } catch (error) {
            console.error('Error loading translations from localStorage:', error);
        }
        return null;
    },

    // Khởi tạo translations với cache và default data
    initializeTranslations: async () => {
        const { currentLanguage } = get();
        
        // Luôn có default data sẵn để hiển thị ngay
        set(state => ({
            translations: {
                ...state.translations,
                [currentLanguage]: state.translations[currentLanguage] || defaultMenuData.content
            }
        }));

        // Thử load từ cache trước
        const cachedTranslations = get().loadTranslationsFromStorage(currentLanguage);
        if (cachedTranslations) {
            set(state => ({
                translations: {
                    ...state.translations,
                    [currentLanguage]: cachedTranslations
                },
                isInitialized: true
            }));
        }

        // Load fresh data ở background (không block UI)
        setTimeout(() => {
            get().loadTranslations(currentLanguage, true); // true = background load
        }, 100);
    },

    // Kiểm tra xem bản dịch đã được tải chưa
    hasTranslation: (language) => {
        const { translations } = get();
        return !!translations[language] && Object.keys(translations[language]).length > 0;
    },

    // Lấy translations hiện tại với fallback
    getCurrentTranslations: () => {
        const { currentLanguage, translations } = get();
        return translations[currentLanguage] || defaultMenuData.content;
    },

    // Chuyển đổi ngôn ngữ
    setLanguage: (language) => {
        set({ currentLanguage: language });
        localStorage.setItem('preferred-language', language);
        
        // Load translations cho ngôn ngữ mới
        get().loadTranslations(language);
    },

    // Cập nhật tiền tệ
    setCurrency: (currencyData) => {

        set({ currency: currencyData });
        localStorage.setItem('preferred-currency', JSON.stringify(currencyData));
    },

    // Tải bản dịch từ API
    loadTranslations: async (language, isBackground = false) => {
        // Nếu không phải background load và đã có translation, skip
        if (!isBackground && get().hasTranslation(language)) {
            return;
        }

        // Check if already loading this language
        if (get().pendingRequests[language]) {
            return;
        }

        // Chỉ hiển thị loading nếu không phải background load
        if (!isBackground) {
            set(state => ({
                loading: true,
                error: null,
                pendingRequests: {
                    ...state.pendingRequests,
                    [language]: true
                }
            }));
        } else {
            set(state => ({
                pendingRequests: {
                    ...state.pendingRequests,
                    [language]: true
                }
            }));
        }

        try {
            const response = await getLangAndCurrency(language.toLowerCase());

            if (!response.content) {
                throw new Error('Failed to load translations');
            }

            // Lưu vào localStorage
            get().saveTranslationsToStorage(language, response.content);

            set((state) => ({
                translations: {
                    ...state.translations,
                    [language]: response.content
                },
                currency: response.currency,
                loading: false,
                isInitialized: true,
                pendingRequests: {
                    ...state.pendingRequests,
                    [language]: false
                }
            }));

        } catch (error) {
            console.error(`Error loading translations for ${language}:`, error);
            set(state => ({
                error: error.message,
                loading: false,
                pendingRequests: {
                    ...state.pendingRequests,
                    [language]: false
                }
            }));
        }
    },

    // Hàm dịch văn bản (hỗ trợ nested keys) với fallback
    t: (key, defaultValue = key) => {
        const { currentLanguage, translations } = get();
        const languageTranslations = translations[currentLanguage] || defaultMenuData.content;

        // Hỗ trợ nested keys như "menu.Jewelry"
        const keys = key.split('.');
        let result = languageTranslations;

        for (const k of keys) {
            result = result?.[k];
        }

        return result || defaultValue;
    },

    // Hàm format giá tiền
    formatPrice: (price, originalCurrency) => {
        const { currency } = get();

        // Chuyển price thành số nếu nó là string
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

        if (isNaN(numericPrice)) {
            return '0';
        }

        // Nếu không truyền originalCurrency, giả định giá gốc là VND (vì từ WooCommerce)
        const sourceCurrency = originalCurrency || 'VND';

        let convertedPrice = numericPrice;

        // Chỉ convert nếu currency hiện tại khác với currency gốc
        if (sourceCurrency !== currency.code) {
            if (sourceCurrency === 'VND' && currency.code === 'USD') {
                // Convert từ VND sang USD
                convertedPrice = numericPrice / currency.rate;
            } else if (sourceCurrency === 'USD' && currency.code === 'VND') {
                // Convert từ USD sang VND
                convertedPrice = numericPrice * currency.rate;
            }
        }

        // Format theo currency hiện tại
        if (currency.code === 'VND') {
            return new Intl.NumberFormat('vi-VN', {
                style: 'currency',
                currency: 'VND',
                currencyDisplay: 'code'
            }).format(convertedPrice);
        } else {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency.code,
                currencyDisplay: 'symbol'
            }).format(convertedPrice);
        }
    }
}));